import React from 'react';
import { createPrefixedRandomID } from '@/utils/random-id';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/admin/components/ui/alert-dialog';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import { Card, CardContent } from '@/admin/components/ui/card';
import { Checkbox } from '@/admin/components/ui/checkbox';
import { FilterBuilder, type ActiveFilterChip, type FilterBuilderOption } from '@/admin/components/common/filter-builder';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/admin/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import { IconEye, IconGift, IconMessages, IconRefresh } from '@tabler/icons-react';
import {
  approveAdminUserOnboarding,
  fetchAdminAddons,
  fetchBillingPlans,
  fetchAdminUser,
  fetchUserDevices,
  fetchUsers,
  grantAdminAddon,
  resetSubscriptionQuota,
  type AdminAddonPlan,
  type AdminDevice,
  type BillingPlan,
  type User,
  type UserBillingPlanSummary,
} from '@/admin/lib/api';
import { UserAvatar } from '../components/common/user-avatar';

const ALL_ONBOARDING_STATUSES = 'all';
const ALL_SUBSCRIPTION_PLANS = 'all';
const ANY_SUBSCRIPTION_PLAN = 'any';
const ALL_INVITE_FILTERS = 'all';
const DEFAULT_ACCOUNT_STATUS_FILTER = 'active';
const DEFAULT_SUBSCRIPTION_FILTER = ALL_SUBSCRIPTION_PLANS;
const DEFAULT_INVITE_FILTER = ALL_INVITE_FILTERS;
type AccountStatusFilter = 'active' | 'deleted' | 'all';
type InviteFilter =
  | 'all'
  | 'any'
  | 'none'
  | 'benefit_none'
  | 'addon'
  | 'subscription';

const onboardingStatusOptions = [
  { value: ALL_ONBOARDING_STATUSES, label: 'All onboarding states' },
  { value: 'invite_required', label: 'Waitlist' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'offered', label: 'Offered' },
  { value: 'completed', label: 'Completed' },
];

const accountStatusOptions: Array<{
  value: AccountStatusFilter;
  label: string;
}> = [
  { value: 'active', label: 'Active accounts' },
  { value: 'deleted', label: 'Deleted accounts' },
  { value: 'all', label: 'All account states' },
];

const inviteFilterOptions: Array<{ value: InviteFilter; label: string }> = [
  { value: 'all', label: 'All invite states' },
  { value: 'any', label: 'Any invite code' },
  { value: 'none', label: 'No invite code' },
  { value: 'benefit_none', label: 'Invite: no gift' },
  { value: 'addon', label: 'Invite: add-on gift' },
  { value: 'subscription', label: 'Invite: subscription gift' },
];

const isAccountStatusFilter = (value: string): value is AccountStatusFilter =>
  value === 'active' || value === 'deleted' || value === 'all';

const isInviteFilter = (value: string): value is InviteFilter =>
  value === 'all' ||
  value === 'any' ||
  value === 'none' ||
  value === 'benefit_none' ||
  value === 'addon' ||
  value === 'subscription';

function userFiltersFromSearchParams(searchParams: URLSearchParams) {
  const accountParam = searchParams.get('account') || '';
  const inviteParam = searchParams.get('invite') || '';
  return {
    q: searchParams.get('q') || '',
    onboarding: searchParams.get('onboarding') || ALL_ONBOARDING_STATUSES,
    account: isAccountStatusFilter(accountParam)
      ? accountParam
      : DEFAULT_ACCOUNT_STATUS_FILTER,
    subscription:
      searchParams.get('subscription') || DEFAULT_SUBSCRIPTION_FILTER,
    invite: isInviteFilter(inviteParam) ? inviteParam : DEFAULT_INVITE_FILTER,
    inviteCode: searchParams.get('invite_code') || '',
  };
}

const onboardingStatusLabel = (status?: string | null) => {
  switch ((status || '').toLowerCase()) {
    case 'invite_required':
      return 'Waitlist';
    case 'in_progress':
      return 'In Progress';
    case 'offered':
      return 'Offered';
    case 'completed':
      return 'Completed';
    default:
      return 'Unknown';
  }
};

const onboardingStatusVariant = (
  status?: string | null
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch ((status || '').toLowerCase()) {
    case 'invite_required':
      return 'outline';
    case 'in_progress':
      return 'secondary';
    case 'offered':
      return 'default';
    case 'completed':
      return 'secondary';
    default:
      return 'outline';
  }
};

const isDeletedUser = (user: User) =>
  user.account_status === 'deleted' || Boolean(user.deleted_at);

const accountStatusLabel = (user: User) =>
  isDeletedUser(user) ? 'Deleted' : 'Active';

const accountStatusVariant = (
  user: User
): 'default' | 'secondary' | 'destructive' | 'outline' =>
  accountStatusLabel(user) === 'Deleted' ? 'destructive' : 'secondary';

const canApproveUser = (user: User) =>
  user.onboarding_status === 'invite_required' && !isDeletedUser(user);

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const formatRelativeTime = (dateString?: string | null) => {
  if (!dateString) return '-';
  const now = new Date();
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

const renderIDValue = (value?: string | null) => {
  if (!value) {
    return <span className="text-muted-foreground">-</span>;
  }
  return (
    <code className="break-all rounded bg-muted px-2 py-1 text-xs">
      {value}
    </code>
  );
};

const creditFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

const formatCredits = (credits: number) => {
  if (!Number.isFinite(credits)) return '0';
  if (credits >= 1_000_000) {
    return `${creditFormatter.format(credits / 1_000_000)}M`;
  }
  if (credits >= 1000) {
    return `${creditFormatter.format(credits / 1000)}K`;
  }
  return new Intl.NumberFormat('en-US').format(credits);
};

const formatPlanLabel = (planCode: string) =>
  planCode
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const sortBillingPlans = (plans: UserBillingPlanSummary[]) =>
  [...plans].sort((left, right) =>
    left.plan_code.localeCompare(right.plan_code)
  );

const BillingPlansInline: React.FC<{
  label: string;
  plans?: UserBillingPlanSummary[];
}> = ({ label, plans = [] }) => {
  const sortedPlans = sortBillingPlans(plans);
  if (sortedPlans.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium uppercase text-muted-foreground">
        {label}
      </div>
      <div className="space-y-1">
        {sortedPlans.map(plan => (
          <div
            key={`${plan.type}-${plan.plan_code}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
          >
            <Badge variant="outline" className="font-normal">
              {formatPlanLabel(plan.plan_code)}
              {plan.grant_count > 1 ? ` x${plan.grant_count}` : ''}
            </Badge>
            <span className="text-muted-foreground">
              {formatCredits(plan.remaining_credits)} /{' '}
              {formatCredits(plan.total_credits)} left
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const UserBillingCell: React.FC<{ user: User }> = ({ user }) => {
  const subscriptionPlans = user.billing?.subscription_plans ?? [];
  const addonPlans = user.billing?.addon_plans ?? [];
  if (subscriptionPlans.length === 0 && addonPlans.length === 0) {
    return <span className="text-sm text-muted-foreground">No credits</span>;
  }
  return (
    <div className="min-w-[240px] space-y-3">
      <BillingPlansInline label="Subscription" plans={subscriptionPlans} />
      <BillingPlansInline label="Add-ons" plans={addonPlans} />
    </div>
  );
};

const inviteBenefitLabel = (user: User) => {
  const invite = user.invite;
  if (!invite) return '';
  switch (invite.benefit_type) {
    case 'one_time_plan':
      return `${invite.benefit_plan_type === 'bonus' ? 'Bonus gift' : 'Add-on gift'}${invite.benefit_plan_code ? `: ${formatPlanLabel(invite.benefit_plan_code)}` : ''}`;
    case 'subscription_months':
      return `Subscription gift${invite.benefit_plan_code ? `: ${formatPlanLabel(invite.benefit_plan_code)}` : ''}${
        invite.benefit_duration_months
          ? `, ${invite.benefit_duration_months}mo`
          : ''
      }`;
    default:
      return 'No gift';
  }
};

const inviteBenefitVariant = (
  user: User
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (user.invite?.benefit_type) {
    case 'one_time_plan':
      return user.invite.benefit_plan_type === 'bonus' ? 'outline' : 'default';
    case 'subscription_months':
      return 'secondary';
    default:
      return 'outline';
  }
};

const UserInviteCell: React.FC<{ user: User }> = ({ user }) => {
  if (!user.invite) {
    return <span className="text-sm text-muted-foreground">No invite</span>;
  }
  return (
    <div className="min-w-[190px] space-y-1">
      <code className="break-all rounded bg-muted px-2 py-1 text-xs">
        {user.invite.code}
      </code>
      <div>
        <Badge variant={inviteBenefitVariant(user)} className="font-normal">
          {inviteBenefitLabel(user)}
        </Badge>
      </div>
    </div>
  );
};

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialFilters] = React.useState(() =>
    userFiltersFromSearchParams(searchParams)
  );
  const [users, setUsers] = React.useState<User[]>([]);
  const [billingPlans, setBillingPlans] = React.useState<BillingPlan[]>([]);
  const [addonPlans, setAddonPlans] = React.useState<AdminAddonPlan[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [resetMessage, setResetMessage] = React.useState<string | null>(null);
  const [addonGrantMessage, setAddonGrantMessage] = React.useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [queryFilter, setQueryFilter] = React.useState(initialFilters.q);
  const [statusFilter, setStatusFilter] = React.useState(
    initialFilters.onboarding
  );
  const [subscriptionPlanFilter, setSubscriptionPlanFilter] = React.useState(
    initialFilters.subscription
  );
  const [inviteFilter, setInviteFilter] = React.useState<InviteFilter>(
    initialFilters.invite
  );
  const [inviteCodeFilter, setInviteCodeFilter] = React.useState(
    initialFilters.inviteCode
  );
  const [accountStatusFilter, setAccountStatusFilter] =
    React.useState<AccountStatusFilter>(initialFilters.account);
  const [selectedUserIds, setSelectedUserIds] = React.useState<Set<string>>(
    () => new Set()
  );
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [resettingQuota, setResettingQuota] = React.useState(false);
  const [addonDialogOpen, setAddonDialogOpen] = React.useState(false);
  const [grantingAddon, setGrantingAddon] = React.useState(false);
  const [selectedAddonPlanCode, setSelectedAddonPlanCode] = React.useState('');
  const [addonGrantReason, setAddonGrantReason] = React.useState('');
  const [addonGrantIdempotencyKey, setAddonGrantIdempotencyKey] =
    React.useState('');
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [selectedUserDevices, setSelectedUserDevices] = React.useState<
    AdminDevice[]
  >([]);
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState<string | null>(null);
  const [approvingUserId, setApprovingUserId] = React.useState<string | null>(
    null
  );
  const listRequestIDRef = React.useRef(0);
  const detailsRequestIDRef = React.useRef(0);
  const loadUsersRef = React.useRef<() => Promise<void>>();

  React.useEffect(() => {
    if (!searchParams.has('account')) {
      const next = new URLSearchParams(searchParams);
      next.set('account', DEFAULT_ACCOUNT_STATUS_FILTER);
      setSearchParams(next, { replace: true });
      return;
    }
    const filters = userFiltersFromSearchParams(searchParams);
    setQueryFilter(filters.q);
    setStatusFilter(filters.onboarding);
    setSubscriptionPlanFilter(filters.subscription);
    setInviteFilter(filters.invite);
    setInviteCodeFilter(filters.inviteCode);
    setAccountStatusFilter(filters.account);
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    let canceled = false;
    Promise.all([fetchBillingPlans(), fetchAdminAddons()])
      .then(([plansResult, addonsResult]) => {
        if (canceled) return;
        setBillingPlans(
          (plansResult.plans || []).filter(plan => plan.type === 'subscription')
        );
        setAddonPlans(addonsResult.addons || []);
        const firstAddon = addonsResult.addons?.[0]?.plan_code ?? '';
        setSelectedAddonPlanCode(current => current || firstAddon);
      })
      .catch(() => {
        if (!canceled) {
          setBillingPlans([]);
          setAddonPlans([]);
        }
      });
    return () => {
      canceled = true;
    };
  }, []);

  const loadUsers = React.useCallback(async () => {
    const requestID = listRequestIDRef.current + 1;
    listRequestIDRef.current = requestID;
    const requestedPage = currentPage;
    let pageCorrectionPending = false;
    setLoading(true);
    setError(null);
    try {
      const offset = (requestedPage - 1) * pageSize;
      const result = await fetchUsers(pageSize, offset, {
        q: queryFilter || undefined,
        onboardingStatus:
          statusFilter === ALL_ONBOARDING_STATUSES ? undefined : statusFilter,
        accountStatus:
          accountStatusFilter === 'all'
            ? undefined
            : accountStatusFilter,
        subscriptionPlanCode:
          subscriptionPlanFilter === ALL_SUBSCRIPTION_PLANS
            ? undefined
            : subscriptionPlanFilter === ANY_SUBSCRIPTION_PLAN
              ? 'all'
              : subscriptionPlanFilter,
        inviteFilter:
          inviteFilter === DEFAULT_INVITE_FILTER ? undefined : inviteFilter,
        inviteCode: inviteCodeFilter || undefined,
      });
      if (listRequestIDRef.current !== requestID) {
        return;
      }
      const nextTotalPages = Math.max(1, Math.ceil(result.total / pageSize));
      if (requestedPage > nextTotalPages) {
        pageCorrectionPending = true;
        setTotal(result.total);
        setUsers([]);
        setCurrentPage(nextTotalPages);
        return;
      }
      setUsers(result.users);
      setTotal(result.total);
    } catch (err) {
      if (listRequestIDRef.current !== requestID) {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      if (listRequestIDRef.current === requestID && !pageCorrectionPending) {
        setLoading(false);
      }
    }
  }, [
    accountStatusFilter,
    currentPage,
    pageSize,
    queryFilter,
    inviteCodeFilter,
    inviteFilter,
    statusFilter,
    subscriptionPlanFilter,
  ]);
  loadUsersRef.current = loadUsers;

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const hasActiveFilters =
    queryFilter.trim().length > 0 ||
    statusFilter !== ALL_ONBOARDING_STATUSES ||
    accountStatusFilter !== 'all' ||
    subscriptionPlanFilter !== DEFAULT_SUBSCRIPTION_FILTER ||
    inviteFilter !== DEFAULT_INVITE_FILTER ||
    inviteCodeFilter.trim().length > 0;
  const visibleUserIds = React.useMemo(
    () => users.map(user => user.id),
    [users]
  );
  const selectedVisibleUserIds = React.useMemo(
    () => visibleUserIds.filter(userId => selectedUserIds.has(userId)),
    [selectedUserIds, visibleUserIds]
  );
  const allVisibleSelected =
    visibleUserIds.length > 0 &&
    visibleUserIds.every(userId => selectedUserIds.has(userId));
  const someVisibleSelected = selectedVisibleUserIds.length > 0;

  React.useEffect(() => {
    setSelectedUserIds(previous => {
      const visible = new Set(visibleUserIds);
      const next = new Set([...previous].filter(userId => visible.has(userId)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleUserIds]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const userFilterOptions = React.useMemo<FilterBuilderOption[]>(() => [
    { key: 'q', label: 'Search text', placeholder: 'email or display name' },
    {
      key: 'onboarding',
      label: 'Onboarding',
      type: 'select',
      values: onboardingStatusOptions
        .filter(option => option.value !== ALL_ONBOARDING_STATUSES)
        .map(option => ({ value: option.value, label: option.label })),
    },
    {
      key: 'account',
      label: 'Account status',
      type: 'select',
      values: accountStatusOptions.map(option => ({
        value: option.value,
        label: option.label,
      })),
    },
    {
      key: 'subscription',
      label: 'Subscription',
      type: 'select',
      values: [
        { value: ANY_SUBSCRIPTION_PLAN, label: 'Any active subscription' },
        ...billingPlans.map(plan => ({
          value: plan.plan_code,
          label: formatPlanLabel(plan.plan_code),
        })),
      ],
    },
    {
      key: 'invite',
      label: 'Invite',
      type: 'select',
      values: inviteFilterOptions
        .filter(option => option.value !== DEFAULT_INVITE_FILTER)
        .map(option => ({ value: option.value, label: option.label })),
    },
    { key: 'invite_code', label: 'Invite code', placeholder: 'ADDON-GIFT' },
  ], [billingPlans]);

  const activeUserFilterChips = React.useMemo<ActiveFilterChip[]>(() => {
    const optionByKey = new Map(userFilterOptions.map(option => [option.key, option]));
    return [
      { key: 'q', value: queryFilter },
      { key: 'onboarding', value: statusFilter === ALL_ONBOARDING_STATUSES ? '' : statusFilter },
      { key: 'account', value: accountStatusFilter === 'all' ? '' : accountStatusFilter },
      { key: 'subscription', value: subscriptionPlanFilter === DEFAULT_SUBSCRIPTION_FILTER ? '' : subscriptionPlanFilter },
      { key: 'invite', value: inviteFilter === DEFAULT_INVITE_FILTER ? '' : inviteFilter },
      { key: 'invite_code', value: inviteCodeFilter },
    ].flatMap(filter => {
      const value = filter.value.trim();
      const option = optionByKey.get(filter.key);
      if (!value || !option) return [];
      return [{
        key: filter.key,
        label: option.label,
        value,
        valueLabel: option.values?.find(item => item.value === value)?.label,
      }];
    });
  }, [
    accountStatusFilter,
    inviteCodeFilter,
    inviteFilter,
    queryFilter,
    statusFilter,
    subscriptionPlanFilter,
    userFilterOptions,
  ]);

  const resetFilterEffects = () => {
    setCurrentPage(1);
    setSelectedUserIds(new Set());
    setResetMessage(null);
    setAddonGrantMessage(null);
  };

  const writeUserFilterParams = (filters: {
    q?: string;
    onboarding?: string;
    account?: AccountStatusFilter;
    subscription?: string;
    invite?: InviteFilter;
    inviteCode?: string;
  }) => {
    const next = new URLSearchParams();
    const values = {
      q: filters.q ?? queryFilter,
      onboarding: filters.onboarding ?? statusFilter,
      account: filters.account ?? accountStatusFilter,
      subscription: filters.subscription ?? subscriptionPlanFilter,
      invite: filters.invite ?? inviteFilter,
      inviteCode: filters.inviteCode ?? inviteCodeFilter,
    };
    if (values.q.trim()) next.set('q', values.q.trim());
    if (values.onboarding !== ALL_ONBOARDING_STATUSES) {
      next.set('onboarding', values.onboarding);
    }
    next.set('account', values.account);
    if (values.subscription !== DEFAULT_SUBSCRIPTION_FILTER) {
      next.set('subscription', values.subscription);
    }
    if (values.invite !== DEFAULT_INVITE_FILTER) {
      next.set('invite', values.invite);
    }
    if (values.inviteCode.trim()) {
      next.set('invite_code', values.inviteCode.trim());
    }
    setSearchParams(next, { replace: true });
  };

  const handleAddFilter = (key: string, value: string) => {
    resetFilterEffects();
    switch (key) {
      case 'q':
        setQueryFilter(value.trim());
        writeUserFilterParams({ q: value.trim() });
        break;
      case 'onboarding':
        setStatusFilter(value);
        writeUserFilterParams({ onboarding: value });
        break;
      case 'account':
        if (isAccountStatusFilter(value)) {
          setAccountStatusFilter(value);
          writeUserFilterParams({ account: value });
        }
        break;
      case 'subscription':
        setSubscriptionPlanFilter(value);
        writeUserFilterParams({ subscription: value });
        break;
      case 'invite':
        if (isInviteFilter(value)) {
          setInviteFilter(value);
          writeUserFilterParams({ invite: value });
        }
        break;
      case 'invite_code':
        setInviteCodeFilter(value.trim());
        writeUserFilterParams({ inviteCode: value.trim() });
        break;
    }
  };

  const handleRemoveFilter = (key: string) => {
    resetFilterEffects();
    switch (key) {
      case 'q':
        setQueryFilter('');
        writeUserFilterParams({ q: '' });
        break;
      case 'onboarding':
        setStatusFilter(ALL_ONBOARDING_STATUSES);
        writeUserFilterParams({ onboarding: ALL_ONBOARDING_STATUSES });
        break;
      case 'account':
        setAccountStatusFilter('all');
        writeUserFilterParams({ account: 'all' });
        break;
      case 'subscription':
        setSubscriptionPlanFilter(DEFAULT_SUBSCRIPTION_FILTER);
        writeUserFilterParams({ subscription: DEFAULT_SUBSCRIPTION_FILTER });
        break;
      case 'invite':
        setInviteFilter(DEFAULT_INVITE_FILTER);
        writeUserFilterParams({ invite: DEFAULT_INVITE_FILTER });
        break;
      case 'invite_code':
        setInviteCodeFilter('');
        writeUserFilterParams({ inviteCode: '' });
        break;
    }
  };

  const handleResetFilters = () => {
    setCurrentPage(1);
    setQueryFilter('');
    setStatusFilter(ALL_ONBOARDING_STATUSES);
    setSubscriptionPlanFilter(DEFAULT_SUBSCRIPTION_FILTER);
    setInviteFilter(DEFAULT_INVITE_FILTER);
    setInviteCodeFilter('');
    setAccountStatusFilter(DEFAULT_ACCOUNT_STATUS_FILTER);
    setSelectedUserIds(new Set());
    setResetMessage(null);
    setAddonGrantMessage(null);
    writeUserFilterParams({
      q: '',
      onboarding: ALL_ONBOARDING_STATUSES,
      account: DEFAULT_ACCOUNT_STATUS_FILTER,
      subscription: DEFAULT_SUBSCRIPTION_FILTER,
      invite: DEFAULT_INVITE_FILTER,
      inviteCode: '',
    });
  };

  const handleViewUser = async (user: User) => {
    const requestID = detailsRequestIDRef.current + 1;
    detailsRequestIDRef.current = requestID;
    setSelectedUser(user);
    setSelectedUserDevices([]);
    setLoadingDetails(true);
    setDetailsError(null);
    try {
      const [userDetails, deviceResult] = await Promise.all([
        fetchAdminUser(user.id),
        fetchUserDevices(user.id),
      ]);
      if (detailsRequestIDRef.current !== requestID) {
        return;
      }
      setSelectedUser(userDetails);
      setSelectedUserDevices(deviceResult.devices);
    } catch (err) {
      if (detailsRequestIDRef.current !== requestID) {
        return;
      }
      setDetailsError(
        err instanceof Error ? err.message : 'Failed to load user details'
      );
      setSelectedUserDevices([]);
    } finally {
      if (detailsRequestIDRef.current === requestID) {
        setLoadingDetails(false);
      }
    }
  };

  const handleViewUserChats = (user: User) => {
    navigate(`/chats?user_id=${encodeURIComponent(user.id)}`);
  };

  const handleApproveWaitlistUser = async (user: User) => {
    const userId = user.id;
    setApprovingUserId(userId);
    setError(null);
    try {
      const updatedUser = await approveAdminUserOnboarding(userId);
      setSelectedUser(prev => (prev?.id === userId ? updatedUser : prev));
      await loadUsersRef.current?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to approve waitlist user'
      );
    } finally {
      setApprovingUserId(null);
    }
  };

  const handleToggleUserSelection = (userId: string, checked: boolean) => {
    setSelectedUserIds(previous => {
      const next = new Set(previous);
      if (checked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  };

  const handleToggleVisibleSelection = (checked: boolean) => {
    setSelectedUserIds(previous => {
      const next = new Set(previous);
      for (const userId of visibleUserIds) {
        if (checked) {
          next.add(userId);
        } else {
          next.delete(userId);
        }
      }
      return next;
    });
  };

  const handleResetSelectedQuota = async () => {
    const userIds = [...selectedUserIds];
    if (userIds.length === 0) {
      return;
    }
    setResettingQuota(true);
    setError(null);
    setResetMessage(null);
    try {
      const result = await resetSubscriptionQuota(userIds);
      const affectedUsers = result.users.filter(user => user.reset_credits > 0);
      setResetMessage(
        `Reset ${formatCredits(result.total_reset_credits)} credits across ${affectedUsers.length} users.`
      );
      setSelectedUserIds(new Set());
      setResetDialogOpen(false);
      await loadUsersRef.current?.();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to reset subscription quota'
      );
    } finally {
      setResettingQuota(false);
    }
  };

  const handleGrantSelectedAddon = async () => {
    const userIds = [...selectedUserIds];
    const planCode = selectedAddonPlanCode.trim();
    if (userIds.length === 0 || !planCode) {
      return;
    }
    setGrantingAddon(true);
    setError(null);
    setAddonGrantMessage(null);
    setResetMessage(null);
    try {
      const result = await grantAdminAddon(userIds, planCode, {
        idempotencyKey: addonGrantIdempotencyKey,
        reason: addonGrantReason.trim() || undefined,
      });
      const grantedUsers = result.users.filter(
        user => user.credits_granted > 0
      );
      const replayedUsers = result.users.filter(user => user.already_granted);
      setAddonGrantMessage(
        `Granted ${formatCredits(result.total_credits_granted)} credits via ${formatPlanLabel(result.plan_code)} to ${grantedUsers.length} users${
          replayedUsers.length > 0
            ? `; ${replayedUsers.length} were already granted`
            : ''
        }.`
      );
      setSelectedUserIds(new Set());
      setAddonDialogOpen(false);
      setAddonGrantReason('');
      setAddonGrantIdempotencyKey('');
      await loadUsersRef.current?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant add-on');
    } finally {
      setGrantingAddon(false);
    }
  };

  const handleAddonDialogOpenChange = (open: boolean) => {
    setAddonDialogOpen(open);
    if (open && !addonGrantIdempotencyKey) {
      setAddonGrantIdempotencyKey(createPrefixedRandomID('admin-users'));
    }
    if (!open && !grantingAddon) {
      setAddonGrantReason('');
      setAddonGrantIdempotencyKey('');
    }
  };

  const handleCloseDialog = (open: boolean) => {
    if (open) return;
    detailsRequestIDRef.current += 1;
    setSelectedUser(null);
    setSelectedUserDevices([]);
    setDetailsError(null);
    setLoadingDetails(false);
  };

  const selectedAddonPlan =
    addonPlans.find(addon => addon.plan_code === selectedAddonPlanCode) ?? null;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto py-4 lg:py-6">
      <div className="shrink-0 px-4 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal">Users</h1>
            <p className="text-sm text-muted-foreground">
              Search users, inspect onboarding state, and manage credits.
            </p>
          </div>
        </div>
      </div>
      <div className="shrink-0 px-4 lg:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="shrink-0 text-sm font-medium text-muted-foreground">
            Total: {total} users
          </span>
          <div className="min-w-[520px] flex-1">
            <FilterBuilder
              options={userFilterOptions}
              activeFilters={activeUserFilterChips}
              onAdd={handleAddFilter}
              onRemove={handleRemoveFilter}
              onClear={handleResetFilters}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={loadUsers}
          >
            Refresh
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={
              selectedUserIds.size === 0 ||
              grantingAddon ||
              addonPlans.length === 0
            }
            onClick={() => handleAddonDialogOpenChange(true)}
          >
            <IconGift className="size-4" />
            Grant add-on
            {selectedUserIds.size > 0 ? ` (${selectedUserIds.size})` : ''}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={selectedUserIds.size === 0 || resettingQuota}
            onClick={() => setResetDialogOpen(true)}
          >
            <IconRefresh className="size-4" />
            Reset quota
            {selectedUserIds.size > 0 ? ` (${selectedUserIds.size})` : ''}
          </Button>

          {!loading && total > 0 && (
            <>
              <span className="text-sm text-muted-foreground">
                {startIndex + 1}-{endIndex} of {total}
              </span>
              <Select
                value={pageSize.toString()}
                onValueChange={value => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>

              <Pagination className="w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(currentPage - 1)}
                      className={
                        currentPage === 1
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPages }).map((_, index) => {
                    const page = index + 1;
                    const showPage =
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1;

                    if (!showPage && totalPages > 5) {
                      const isLeftGap =
                        page === currentPage - 2 && currentPage > 3;
                      const isRightGap =
                        page === currentPage + 2 &&
                        currentPage < totalPages - 2;
                      if (isLeftGap || isRightGap) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    }

                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          isActive={currentPage === page}
                          onClick={() => handlePageChange(page)}
                          className="h-8 w-8 cursor-pointer p-0"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

              <PaginationItem>
                <PaginationNext
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={
                    currentPage === totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
                </PaginationContent>
              </Pagination>
            </>
          )}
        </div>
      </div>

      <div className="px-4 lg:px-6">
        {resetMessage ? (
          <div className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {resetMessage}
          </div>
        ) : null}
        {addonGrantMessage ? (
          <div className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {addonGrantMessage}
          </div>
        ) : null}
        <Card>
          <CardContent className="p-4">
            <div className="overflow-hidden rounded-md border">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={
                          allVisibleSelected
                            ? true
                            : someVisibleSelected
                              ? 'indeterminate'
                              : false
                        }
                        onCheckedChange={checked =>
                          handleToggleVisibleSelection(checked === true)
                        }
                        aria-label="Select visible users"
                      />
                    </TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Display Name</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Invite</TableHead>
                    <TableHead>Billing</TableHead>
                    <TableHead>Onboarding</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Recent Login</TableHead>
                    <TableHead className="w-[180px] text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="h-32 text-center text-muted-foreground"
                      >
                        Loading users...
                      </TableCell>
                    </TableRow>
                  )}
                  {error && (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="h-32 text-center text-destructive"
                      >
                        {error}
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && !error && users.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="h-32 text-center text-muted-foreground"
                      >
                        {hasActiveFilters
                          ? 'No users match the current filters'
                          : 'No users yet'}
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    !error &&
                    users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUserIds.has(user.id)}
                            onCheckedChange={checked =>
                              handleToggleUserSelection(
                                user.id,
                                checked === true
                              )
                            }
                            aria-label={`Select ${user.email}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <UserAvatar user={user} />
                            <span className="font-medium">{user.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.display_name || (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge variant={accountStatusVariant(user)}>
                              {accountStatusLabel(user)}
                            </Badge>
                            {user.deleted_at ? (
                              <span className="text-xs text-muted-foreground">
                                {formatDateTime(user.deleted_at)}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <UserInviteCell user={user} />
                        </TableCell>
                        <TableCell>
                          <UserBillingCell user={user} />
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={onboardingStatusVariant(
                              user.onboarding_status
                            )}
                          >
                            {onboardingStatusLabel(user.onboarding_status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">
                              {formatRelativeTime(user.created_at)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDateTime(user.created_at)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">
                              {formatRelativeTime(user.last_login_at)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDateTime(user.last_login_at)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canApproveUser(user) ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApproveWaitlistUser(user)}
                                disabled={approvingUserId === user.id}
                              >
                                {approvingUserId === user.id
                                  ? 'Approving...'
                                  : 'Approve'}
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewUserChats(user)}
                              aria-label={`Open chats for ${user.email}`}
                            >
                              <IconMessages className="size-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewUser(user)}
                              aria-label={`View ${user.email}`}
                            >
                              <IconEye className="size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={addonDialogOpen} onOpenChange={handleAddonDialogOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Grant add-on</DialogTitle>
            <DialogDescription>
              Grant a one-time add-on to selected accounts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              {selectedUserIds.size} selected user
              {selectedUserIds.size === 1 ? '' : 's'}
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Add-on</div>
              <Select
                value={selectedAddonPlanCode}
                onValueChange={setSelectedAddonPlanCode}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select add-on" />
                </SelectTrigger>
                <SelectContent>
                  {addonPlans.map(addon => (
                    <SelectItem key={addon.plan_code} value={addon.plan_code}>
                      {formatPlanLabel(addon.plan_code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAddonPlan ? (
                <div className="text-xs text-muted-foreground">
                  {formatCredits(selectedAddonPlan.credits_grant)} credits
                  {selectedAddonPlan.display_copy
                    ? ` · ${selectedAddonPlan.display_copy}`
                    : ''}
                </div>
              ) : null}
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Reason</div>
              <Input
                value={addonGrantReason}
                onChange={event => setAddonGrantReason(event.target.value)}
                placeholder="invite credit backfill"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={grantingAddon}
              onClick={() => setAddonDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                grantingAddon ||
                selectedUserIds.size === 0 ||
                !selectedAddonPlanCode ||
                !addonGrantIdempotencyKey
              }
              onClick={handleGrantSelectedAddon}
            >
              {grantingAddon ? 'Granting...' : 'Grant add-on'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset subscription quota?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset used credits for active subscription grants and
              signup bonus (free) grants. Add-on grants and daily usage records
              will remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            {selectedUserIds.size} selected user
            {selectedUserIds.size === 1 ? '' : 's'}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resettingQuota}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={event => {
                event.preventDefault();
                handleResetSelectedQuota();
              }}
              disabled={resettingQuota || selectedUserIds.size === 0}
            >
              {resettingQuota ? 'Resetting...' : 'Reset quota'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(selectedUser)} onOpenChange={handleCloseDialog}>
        {selectedUser ? (
          <DialogContent className="max-h-[85vh] max-w-3xl overflow-auto">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
              <DialogDescription>{selectedUser.id}</DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border p-4">
                <UserAvatar
                  user={selectedUser}
                  showName={true}
                  showEmail={true}
                  description={
                    <Badge
                      variant={onboardingStatusVariant(
                        selectedUser.onboarding_status
                      )}
                      className="mt-1"
                    >
                      {onboardingStatusLabel(selectedUser.onboarding_status)}
                    </Badge>
                  }
                  size="lg"
                />
                {canApproveUser(selectedUser) ? (
                  <Button
                    variant="outline"
                    onClick={() => handleApproveWaitlistUser(selectedUser)}
                    disabled={approvingUserId === selectedUser.id}
                  >
                    {approvingUserId === selectedUser.id
                      ? 'Approving...'
                      : 'Approve waitlist user'}
                  </Button>
                ) : null}
              </div>

              {detailsError ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  {detailsError}
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1 rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Account Status
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={accountStatusVariant(selectedUser)}>
                      {accountStatusLabel(selectedUser)}
                    </Badge>
                    {selectedUser.deleted_at ? (
                      <span className="text-sm text-muted-foreground">
                        Deleted {formatDateTime(selectedUser.deleted_at)}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="space-y-1 rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Created At
                  </div>
                  <div>{formatDateTime(selectedUser.created_at)}</div>
                </div>
                <div className="space-y-1 rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Recent Login
                  </div>
                  <div>{formatDateTime(selectedUser.last_login_at)}</div>
                </div>
                <div className="space-y-2 rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Onboarding Conversation
                  </div>
                  <div>
                    {renderIDValue(selectedUser.onboarding_conversation_id)}
                  </div>
                </div>
                <div className="space-y-2 rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Active Offer
                  </div>
                  <div>{renderIDValue(selectedUser.active_offer_id)}</div>
                </div>
                <div className="space-y-1 rounded-lg border p-4 md:col-span-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Onboarding Completed At
                  </div>
                  <div>
                    {formatDateTime(selectedUser.onboarding_completed_at)}
                  </div>
                </div>
                <div className="space-y-1 rounded-lg border p-4 md:col-span-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Admin Note
                  </div>
                  <div className="whitespace-pre-wrap break-words">
                    {selectedUser.admin_note || (
                      <span className="text-muted-foreground">
                        No admin note
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3 rounded-lg border p-4">
                <div className="text-sm font-medium text-muted-foreground">
                  Billing
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <BillingPlansInline
                    label="Subscription"
                    plans={selectedUser.billing?.subscription_plans}
                  />
                  <BillingPlansInline
                    label="Add-ons"
                    plans={selectedUser.billing?.addon_plans}
                  />
                </div>
                {(selectedUser.billing?.subscription_plans?.length ?? 0) ===
                  0 &&
                (selectedUser.billing?.addon_plans?.length ?? 0) === 0 ? (
                  <span className="text-sm text-muted-foreground">
                    No active credit grants
                  </span>
                ) : null}
              </div>

              <div className="space-y-3 rounded-lg border p-4">
                <div className="text-sm font-medium text-muted-foreground">
                  Devices
                </div>
                {loadingDetails ? (
                  <span className="text-sm text-muted-foreground">
                    Loading devices...
                  </span>
                ) : selectedUserDevices.length > 0 ? (
                  <div className="space-y-2">
                    {selectedUserDevices.map(device => (
                      <div
                        key={device.id}
                        className="flex items-center justify-between rounded-md border p-3"
                      >
                        <div>
                          <span className="text-sm font-medium">
                            {device.name}
                          </span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {device.platform}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {device.app_version || 'unknown'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    No devices registered
                  </span>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleCloseDialog(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
};
